import React, { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { TableCell, TableKit } from '@tiptap/extension-table';
import {
    TableCellWithBorder,
    TableHeaderWithBorder,
    ParagraphWithPadding,
    HeadingWithPadding,
} from 'adapter/in/reports/contexts/TextBlockTableExtensions';

export const TiptapEditorExtensions = [
    StarterKit.configure({
        paragraph: false,
        heading: false,
    }),
    ParagraphWithPadding,
    HeadingWithPadding,
    Superscript,
    Subscript,
    TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: null,
    }),
    TableKit.configure({
        table: {
            resizable: true,
        },
        tableCell: false,
        tableHeader: false,
    }),
    TableCellWithBorder,
    TableHeaderWithBorder,
    Image.configure({
        allowBase64: true,
        resize: {
            enabled: true,
            minWidth: 50,
            minHeight: 50,
            alwaysPreserveAspectRatio: true,
        },
    }),
    TextStyle,
    Color,
    FontSize.configure({
        types: ['textStyle', 'tableHeader', 'tableCell'],
    }),
    Highlight.configure({ multicolor: true }),
];

export type EditorWithIdType = {
    editor: Editor | null;
    textBlockId: string | null;
};

export enum ACTION_TYPE {
    ADD_EDITOR = 'ADD_EDITOR',
    SET_EDITOR_BEING_EDITED = 'SET_EDITOR_BEING_EDITED',
    SET_SAVED_CONTENT = 'SET_SAVED_CONTENT',
    CLEAR_SAVED_CONTENT = 'CLEAR_SAVED_CONTENT',
}

export type TextBlockDataState = {
    editors: EditorWithIdType[] | null;
    editorBeingEdited: EditorWithIdType | null;
    savedContentByTextBlockId: Record<string, string>;
};

export type AddEditor = {
    type: ACTION_TYPE.ADD_EDITOR;
    payload: EditorWithIdType;
};

export type SetEditorBeingEdited = {
    type: ACTION_TYPE.SET_EDITOR_BEING_EDITED;
    payload: EditorWithIdType | null;
};

export type SetSavedContent = {
    type: ACTION_TYPE.SET_SAVED_CONTENT;
    payload: { textBlockId: string; content: string };
};

export type ClearSavedContent = {
    type: ACTION_TYPE.CLEAR_SAVED_CONTENT;
    payload: { textBlockId: string };
};

type Actions = AddEditor | SetEditorBeingEdited | SetSavedContent | ClearSavedContent;

export const initialState: TextBlockDataState = {
    editors: null,
    editorBeingEdited: null,
    savedContentByTextBlockId: {},
};

export const useTextBlockStateReducer = (state: TextBlockDataState, action: Actions): TextBlockDataState => {
    switch (action.type) {
        case ACTION_TYPE.ADD_EDITOR: {
            const others = (state.editors ?? []).filter((e) => e.textBlockId !== action.payload.textBlockId);
            return { ...state, editors: [...others, action.payload] };
        }
        case ACTION_TYPE.SET_EDITOR_BEING_EDITED: {
            return { ...state, editorBeingEdited: action.payload };
        }
        case ACTION_TYPE.SET_SAVED_CONTENT: {
            return {
                ...state,
                savedContentByTextBlockId: {
                    ...state.savedContentByTextBlockId,
                    [action.payload.textBlockId]: action.payload.content,
                },
            };
        }
        case ACTION_TYPE.CLEAR_SAVED_CONTENT: {
            const rest = { ...state.savedContentByTextBlockId };
            delete rest[action.payload.textBlockId];
            return { ...state, savedContentByTextBlockId: rest };
        }
        default:
            return state;
    }
};

type TextBlockStateDispatch = {
    addEditor: (editorPayload: EditorWithIdType) => void;
    setEditorBeingEdited: (textBlockId: string | null) => void;
    setSavedContent: (textBlockId: string, content: string) => void;
    clearSavedContent: (textBlockId: string) => void;
};

export type TextBlockProviderProps = {
    children: ReactNode;
};

export const TextBlockDataStateDispatchContext = createContext({} as TextBlockStateDispatch);
const TextBlockDataStateContext = createContext({} as TextBlockDataState);

const dispatchTextBlockData = (): TextBlockStateDispatch => useContext(TextBlockDataStateDispatchContext);
const useTextBlockData = (): TextBlockDataState => useContext(TextBlockDataStateContext);

const TextBlockDataProvider = (props: TextBlockProviderProps) => {
    const { children } = props;
    const [state, dispatch] = useReducer(useTextBlockStateReducer, initialState);

    const addEditor = React.useCallback((editorPayload: EditorWithIdType) => {
        dispatch({ type: ACTION_TYPE.ADD_EDITOR, payload: editorPayload });
    }, []);

    const setEditorBeingEdited = useCallback(
        (textBlockId: string | null) => {
            const editorWithId = state.editors?.find((e) => e.textBlockId === textBlockId) ?? null;
            dispatch({ type: ACTION_TYPE.SET_EDITOR_BEING_EDITED, payload: editorWithId });
        },
        [state.editors]
    );

    const setSavedContent = useCallback((textBlockId: string, content: string) => {
        dispatch({ type: ACTION_TYPE.SET_SAVED_CONTENT, payload: { textBlockId, content } });
    }, []);

    const clearSavedContent = useCallback((textBlockId: string) => {
        dispatch({ type: ACTION_TYPE.CLEAR_SAVED_CONTENT, payload: { textBlockId } });
    }, []);

    const dispatchValue = useMemo(() => ({ addEditor, setEditorBeingEdited, setSavedContent, clearSavedContent }), [
        addEditor,
        setEditorBeingEdited,
        setSavedContent,
        clearSavedContent,
    ]);

    return (
        <TextBlockDataStateContext.Provider value={state}>
            <TextBlockDataStateDispatchContext.Provider value={dispatchValue}>{children}</TextBlockDataStateDispatchContext.Provider>
        </TextBlockDataStateContext.Provider>
    );
};

export { TextBlockDataProvider, dispatchTextBlockData, useTextBlockData };
